"use client";

import { c as _c } from "react/compiler-runtime";
import { useDeferredValue, useEffect, useState, useTransition } from "react";
import Image from "next/image";
import Link from "next/link";
import { Activity, BookOpenText, BriefcaseBusiness, CalendarClock, CalendarRange, ChevronRight, ClipboardList, ContactRound, FileClock, FolderKanban, HeartHandshake, House, LibraryBig, Mail, Menu, MessageSquareMore, NotebookPen, PanelLeftClose, PanelLeftOpen, PenLine, Search, Settings2, ShieldCheck, Sparkles, Stethoscope, Wrench, UsersRound } from "lucide-react";
import { LogoutButton } from "@/shared/ui/logout-button";
import { NotificationBell } from "@/features/notifications";
import { DashboardSearch } from "@/features/search";
import { useFcmSetup } from "@/features/notifications/presentation/hooks/useFcmSetup";
import { cn } from "@/shared/lib/utils";
import { useResponsive } from "@/shared/hooks/use-responsive";
import type { DashboardActivityColumn, DashboardActivityItem, DashboardIconKey, DashboardPageContent, DashboardStat, DashboardTaskColumn, DashboardTaskItem, DashboardTone } from "@/shared/types/dashboard-shell";
const ICON_MAP = {
  activity: Activity,
  "book-open": BookOpenText,
  briefcase: BriefcaseBusiness,
  calendar: CalendarRange,
  "calendar-clock": CalendarClock,
  clipboard: ClipboardList,
  contacts: ContactRound,
  "file-clock": FileClock,
  folder: FolderKanban,
  "heart-handshake": HeartHandshake,
  home: House,
  library: LibraryBig,
  mail: Mail,
  messages: MessageSquareMore,
  notes: NotebookPen,
  search: Search,
  settings: Settings2,
  shield: ShieldCheck,
  sparkles: Sparkles,
  stethoscope: Stethoscope,
  tools: Wrench,
  users: UsersRound,
  pen: PenLine
} satisfies Record<DashboardIconKey, React.ComponentType<{
  className?: string;
}>>;
const TONE_STYLES = {
  amber: {
    avatar: "bg-amber-100 text-amber-700",
    dot: "bg-amber-500",
    pill: "bg-amber-50 text-amber-700 ring-amber-200"
  },
  blue: {
    avatar: "bg-blue-100 text-blue-700",
    dot: "bg-blue-500",
    pill: "bg-blue-50 text-blue-700 ring-blue-200"
  },
  cyan: {
    avatar: "bg-cyan-100 text-cyan-700",
    dot: "bg-cyan-500",
    pill: "bg-cyan-50 text-cyan-700 ring-cyan-200"
  },
  emerald: {
    avatar: "bg-emerald-100 text-emerald-700",
    dot: "bg-emerald-500",
    pill: "bg-emerald-50 text-emerald-700 ring-emerald-200"
  },
  rose: {
    avatar: "bg-rose-100 text-rose-700",
    dot: "bg-rose-500",
    pill: "bg-rose-50 text-rose-700 ring-rose-200"
  },
  slate: {
    avatar: "bg-slate-100 text-slate-700",
    dot: "bg-slate-500",
    pill: "bg-slate-100 text-slate-700 ring-slate-200"
  },
  violet: {
    avatar: "bg-violet-100 text-violet-700",
    dot: "bg-violet-500",
    pill: "bg-violet-50 text-violet-700 ring-violet-200"
  }
} satisfies Record<DashboardTone, {
  avatar: string;
  dot: string;
  pill: string;
}>;
interface DashboardShellProps {
  children?: React.ReactNode;
  content: DashboardPageContent;
}
interface DashboardColumnState<TItem> {
  id: string;
  items: TItem[];
}
function matchesQuery(query: string, values: string[]): boolean {
  if (!query) {
    return true;
  }
  const normalizedValues = values.join(" ").toLowerCase();
  return normalizedValues.includes(query);
}
function filterActivityColumns(columns: DashboardActivityColumn[], query: string): DashboardColumnState<DashboardActivityItem>[] {
  return columns.map(column => ({
    id: column.id,
    items: column.items.filter(item => matchesQuery(query, [item.title, item.subtitle, item.meta, item.badge?.label ?? ""]))
  }));
}
function filterTaskColumns(columns: DashboardTaskColumn[], query: string): DashboardColumnState<DashboardTaskItem>[] {
  return columns.map(column => ({
    id: column.id,
    items: column.items.filter(item => matchesQuery(query, [item.title, item.description, item.meta]))
  }));
}
function getStatTrendClass(trend: DashboardStat["trend"]): string {
  if (trend === "positive") {
    return "text-emerald-600";
  }
  if (trend === "warning") {
    return "text-amber-600";
  }
  return "text-slate-500";
}
const SIDEBAR_LOGO_FULL = "https://res.cloudinary.com/dsalsyu0z/image/upload/v1771236641/Frame_2_njt372.png";
const SIDEBAR_LOGO_ICON = "https://res.cloudinary.com/dsalsyu0z/image/upload/v1772867269/ank-admin-blog/c2ebocurkpvydoihl1sd.png";
function UserAvatar(t0) {
  const $ = _c(8);
  const {
    avatarUrl,
    imgClassName,
    shortName
  } = t0;
  if (avatarUrl) {
    let t1;
    if ($[0] !== imgClassName) {
      t1 = cn("object-cover", imgClassName);
      $[0] = imgClassName;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== avatarUrl || $[3] !== shortName || $[4] !== t1) {
      t2 = <Image alt={shortName} className={t1} height={44} src={avatarUrl} width={44} />;
      $[2] = avatarUrl;
      $[3] = shortName;
      $[4] = t1;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  let t1;
  if ($[6] !== shortName) {
    t1 = <span>{shortName}</span>;
    $[6] = shortName;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
function SidebarItem(t0) {
  const $ = _c(25);
  const {
    href,
    icon,
    isActive,
    isCollapsed,
    label,
    badge
  } = t0;
  const Icon = ICON_MAP[icon];
  const t1 = isCollapsed ? "size-10 justify-center rounded-full p-0" : "rounded-2xl px-3 py-2.5";
  const t2 = isActive ? "bg-[linear-gradient(135deg,var(--primary)_0%,var(--accent)_100%)] text-white" : "text-slate-600 hover:bg-white/90 hover:text-slate-950";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = cn("group flex items-center gap-3 text-sm font-medium transition-all duration-300", t1, t2);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = !isActive && "group-hover:scale-105";
  let t5;
  if ($[3] !== t4) {
    t5 = cn("size-4.5 shrink-0 transition-transform duration-300", t4);
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== Icon || $[6] !== t5) {
    t6 = <Icon className={t5} />;
    $[5] = Icon;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = isCollapsed ? "hidden" : "w-auto opacity-100";
  let t8;
  if ($[8] !== t7) {
    t8 = cn("flex min-w-0 flex-1 items-center justify-between gap-2 overflow-hidden transition-[width,opacity] duration-300", t7);
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== label) {
    t9 = <span className="truncate">{label}</span>;
    $[10] = label;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== badge || $[13] !== isActive) {
    t10 = badge ? <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold", isActive ? "bg-white/18 text-white" : "bg-blue-50 text-blue-700 ring-1 ring-blue-100")}>{badge}</span> : null;
    $[12] = badge;
    $[13] = isActive;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10 || $[16] !== t8 || $[17] !== t9) {
    t11 = <span className={t8}>{t9}{t10}</span>;
    $[15] = t10;
    $[16] = t8;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== href || $[20] !== label || $[21] !== t11 || $[22] !== t3 || $[23] !== t6) {
    t12 = <Link className={t3} href={href} title={label}>{t6}{t11}</Link>;
    $[19] = href;
    $[20] = label;
    $[21] = t11;
    $[22] = t3;
    $[23] = t6;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}
function StatCard(t0) {
  const $ = _c(23);
  const {
    stat
  } = t0;
  const Icon = ICON_MAP[stat.icon];
  let t1;
  if ($[0] !== stat.label) {
    t1 = <p className="text-sm font-medium text-slate-500">{stat.label}</p>;
    $[0] = stat.label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== Icon) {
    t2 = <Icon className="size-5 text-slate-900" />;
    $[2] = Icon;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== stat.value) {
    t3 = <span className="text-3xl font-semibold tracking-tight text-slate-950">{stat.value}</span>;
    $[4] = stat.value;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="flex items-center gap-3">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t4) {
    t5 = <div className="space-y-2">{t1}{t4}</div>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ChevronRight className="mt-1 size-4 text-slate-300" />;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5) {
    t7 = <div className="flex items-start justify-between gap-3">{t5}{t6}</div>;
    $[13] = t5;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== stat.trend) {
    t8 = cn("mt-5 text-sm font-medium", getStatTrendClass(stat.trend));
    $[15] = stat.trend;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== stat.delta || $[18] !== t8) {
    t9 = <p className={t8}>{stat.delta}</p>;
    $[17] = stat.delta;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t7 || $[21] !== t9) {
    t10 = <article className="rounded-[1.65rem] border border-white/60 bg-white/92 px-5 py-4 shadow-[0_20px_50px_rgba(15,23,42,0.05)] ring-1 ring-slate-200/60">{t7}{t9}</article>;
    $[20] = t7;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}
function ActivityColumnCard(t0) {
  const $ = _c(19);
  const {
    column,
    emptyStateLabel,
    items
  } = t0;
  const Icon = ICON_MAP[column.icon];
  let t1;
  if ($[0] !== Icon) {
    t1 = <span className="inline-flex size-8 items-center justify-center rounded-full bg-slate-100 text-slate-700"><Icon className="size-4" /></span>;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== column.title || $[3] !== t1) {
    t2 = <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">{t1}{column.title}</div>;
    $[2] = column.title;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ChevronRight className="size-4" />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== column.actionLabel) {
    t4 = <button aria-label={column.actionLabel} className="inline-flex size-8 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-900" type="button">{t3}</button>;
    $[6] = column.actionLabel;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t4) {
    t5 = <div className="flex items-center justify-between gap-3">{t2}{t4}</div>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== emptyStateLabel || $[12] !== items) {
    t6 = items.length ? items.map(_temp2) : <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-6 text-sm text-slate-500">{emptyStateLabel}</div>;
    $[11] = emptyStateLabel;
    $[12] = items;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t6) {
    t7 = <div className="mt-4 space-y-3">{t6}</div>;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t5 || $[17] !== t7) {
    t8 = <section className="rounded-[1.8rem] border border-white/65 bg-white/94 p-5 shadow-[0_24px_64px_rgba(15,23,42,0.06)] ring-1 ring-slate-200/60">{t5}{t7}</section>;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}
function _temp2(item) {
  const toneStyles = TONE_STYLES[item.tone];
  return <article className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50/72 px-3 py-3" key={item.id}><div className={cn("inline-flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold", toneStyles.avatar)}>{item.title.split(" ").filter(Boolean).slice(0, 2).map(_temp).join("")}</div><div className="min-w-0 flex-1"><div className="flex items-start justify-between gap-3"><div className="min-w-0"><p className="truncate text-sm font-semibold text-slate-950">{item.title}</p><p className="truncate text-xs text-slate-500">{item.subtitle}</p></div>{item.badge ? <span className={cn("shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1", TONE_STYLES[item.badge.tone].pill)}>{item.badge.label}</span> : null}</div><p className="mt-2 text-xs font-medium text-slate-400">{item.meta}</p></div></article>;
}
function _temp(part) {
  return part.charAt(0).toUpperCase();
}
function TaskColumnCard(t0) {
  const $ = _c(31);
  const {
    column,
    emptyStateLabel,
    isExpanded,
    items
  } = t0;
  let t1;
  if ($[0] !== isExpanded || $[1] !== items) {
    t1 = isExpanded ? items : items.slice(0, 2);
    $[0] = isExpanded;
    $[1] = items;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const visibleItems = t1;
  const toneStyles = TONE_STYLES[column.tone];
  let t2;
  if ($[3] !== toneStyles.dot) {
    t2 = cn("size-2 rounded-full", toneStyles.dot);
    $[3] = toneStyles.dot;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <span className={t2} />;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== column.title) {
    t4 = <p className="text-sm font-semibold text-slate-950">{column.title}</p>;
    $[7] = column.title;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== column.count) {
    t5 = <p className="text-xs text-slate-400">{column.count}</p>;
    $[9] = column.count;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <div>{t4}{t5}</div>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t3 || $[15] !== t6) {
    t7 = <div className="flex items-center gap-2">{t3}{t6}</div>;
    $[14] = t3;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="text-lg leading-none">+</span>;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== column.actionLabel) {
    t9 = <button aria-label={column.actionLabel} className="inline-flex size-8 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-900" type="button">{t8}</button>;
    $[18] = column.actionLabel;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t7 || $[21] !== t9) {
    t10 = <div className="flex items-center justify-between gap-3">{t7}{t9}</div>;
    $[20] = t7;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== emptyStateLabel || $[24] !== visibleItems) {
    t11 = visibleItems.length ? visibleItems.map(_temp3) : <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-6 text-sm text-slate-500">{emptyStateLabel}</div>;
    $[23] = emptyStateLabel;
    $[24] = visibleItems;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11) {
    t12 = <div className="mt-4 space-y-3">{t11}</div>;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t10 || $[29] !== t12) {
    t13 = <section className="rounded-[1.8rem] border border-white/65 bg-white/94 p-5 shadow-[0_24px_64px_rgba(15,23,42,0.06)] ring-1 ring-slate-200/60">{t10}{t12}</section>;
    $[28] = t10;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  return t13;
}
function _temp3(item) {
  return <article className="rounded-2xl border border-slate-100 bg-slate-50/72 px-4 py-4" key={item.id}><div className="flex items-start justify-between gap-3"><p className="text-sm font-semibold text-slate-950">{item.title}</p><span className="text-slate-300">⋮</span></div><p className="mt-2 text-sm leading-6 text-slate-600">{item.description}</p><p className="mt-3 text-xs font-medium text-slate-400">{item.meta}</p></article>;
}
export function DashboardShell(t0) {
  const $ = _c(181);
  const {
    children,
    content
  } = t0;
  useFcmSetup();
  const {
    isSidebarCollapsed,
    isSidebarOpen: isMobileSidebarOpen,
    setSidebarOpen: setIsMobileSidebarOpen,
    toggleSidebar
  } = useResponsive();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isTasksExpanded, setIsTasksExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [, startSearchTransition] = useTransition();
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const normalizedQuery = deferredSearchQuery.trim().toLowerCase();
  const filteredActivityColumns = filterActivityColumns(content.activity.columns, normalizedQuery);
  const filteredTaskColumns = filterTaskColumns(content.tasks.columns, normalizedQuery);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const syncScrollState = function syncScrollState() {
        setIsScrolled(window.scrollY > 36);
      };
      syncScrollState();
      window.addEventListener("scroll", syncScrollState, {
        passive: true
      });
      return () => {
        window.removeEventListener("scroll", syncScrollState);
      };
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const t3 = "min-h-screen bg-[radial-gradient(circle_at_top,#d7e7ff_0%,#eef4ff_34%,#f7fbff_58%,#f8fafc_100%)] px-2 py-2 text-slate-950 sm:px-3 sm:py-3 lg:px-0 lg:py-0";
  let t4;
  if ($[2] !== content.header.closeSidebarLabel || $[3] !== isMobileSidebarOpen || $[4] !== setIsMobileSidebarOpen) {
    t4 = isMobileSidebarOpen ? <button aria-label={content.header.closeSidebarLabel} className="fixed inset-0 z-30 bg-slate-950/30 backdrop-blur-sm lg:hidden" onClick={() => setIsMobileSidebarOpen(false)} type="button" /> : null;
    $[2] = content.header.closeSidebarLabel;
    $[3] = isMobileSidebarOpen;
    $[4] = setIsMobileSidebarOpen;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = "w-full";
  const t6 = isSidebarCollapsed ? "w-[80px]" : "w-[230px]";
  const t7 = isMobileSidebarOpen ? "translate-x-0" : "-translate-x-[calc(100%+1rem)]";
  let t8;
  if ($[6] !== t6 || $[7] !== t7) {
    t8 = cn("fixed inset-y-2 left-2 z-40 flex h-[calc(100vh-1rem)] shrink-0 flex-col rounded-[2rem] border border-white/70 bg-white/88 p-3 shadow-[0_28px_80px_rgba(15,23,42,0.08)] ring-1 ring-slate-200/60 backdrop-blur-xl transition-all duration-300 sm:inset-y-3 sm:left-3 sm:h-[calc(100vh-1.5rem)] lg:inset-y-4 lg:left-4 lg:h-[calc(100vh-2rem)] lg:translate-x-0 lg:rounded-xl lg:p-4", t6, t7, "lg:translate-x-0");
    $[6] = t6;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const t9 = isSidebarCollapsed ? "justify-center" : "justify-between";
  let t10;
  if ($[9] !== t9) {
    t10 = cn("flex items-center gap-3 px-2 pt-2 pb-4", t9);
    $[9] = t9;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const t11 = isSidebarCollapsed ? "justify-center" : "gap-3";
  let t12;
  if ($[11] !== t11) {
    t12 = cn("flex min-w-0 items-center", t11);
    $[11] = t11;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  const t13 = content.brand.href;
  const t14 = isSidebarCollapsed ? "w-9 opacity-100" : "w-0 opacity-0";
  let t15;
  if ($[13] !== t14) {
    t15 = cn("shrink-0 overflow-hidden transition-[width,opacity] duration-300", t14);
    $[13] = t14;
    $[14] = t15;
  } else {
    t15 = $[14];
  }
  let t16;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Image alt="ItsHealer" className="h-9 w-9" height={36} src={SIDEBAR_LOGO_ICON} width={36} />;
    $[15] = t16;
  } else {
    t16 = $[15];
  }
  let t17;
  if ($[16] !== t15) {
    t17 = <div className={t15}>{t16}</div>;
    $[16] = t15;
    $[17] = t17;
  } else {
    t17 = $[17];
  }
  const t18 = isSidebarCollapsed ? "w-0 opacity-0" : "w-auto opacity-100";
  let t19;
  if ($[18] !== t18) {
    t19 = cn("min-w-0 overflow-hidden transition-[width,opacity] duration-300", t18);
    $[18] = t18;
    $[19] = t19;
  } else {
    t19 = $[19];
  }
  let t20;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Image alt="ItsHealer" className="h-8 w-auto" height={32} src={SIDEBAR_LOGO_FULL} width={120} />;
    $[20] = t20;
  } else {
    t20 = $[20];
  }
  let t21;
  if ($[21] !== t19) {
    t21 = <div className={t19}>{t20}</div>;
    $[21] = t19;
    $[22] = t21;
  } else {
    t21 = $[22];
  }
  let t22;
  if ($[23] !== content.brand.href || $[24] !== t12 || $[25] !== t17 || $[26] !== t21) {
    t22 = <Link className={t12} href={t13}>{t17}{t21}</Link>;
    $[23] = content.brand.href;
    $[24] = t12;
    $[25] = t17;
    $[26] = t21;
    $[27] = t22;
  } else {
    t22 = $[27];
  }
  let t23;
  if ($[28] !== t10 || $[29] !== t22) {
    t23 = <div className={t10}>{t22}</div>;
    $[28] = t10;
    $[29] = t22;
    $[30] = t23;
  } else {
    t23 = $[30];
  }
  let t24;
  if ($[31] !== content.sidebar.groups || $[32] !== isSidebarCollapsed) {
    let t25;
    if ($[34] !== isSidebarCollapsed) {
      t25 = group => <div className="space-y-2" key={group.id}>{group.label ? <p className={cn("px-2 text-[11px] font-semibold tracking-[0.18em] text-slate-400 uppercase transition-opacity duration-300", isSidebarCollapsed && "opacity-0")}>{group.label}</p> : null}<div className="space-y-1">{group.items.map(item => <SidebarItem badge={item.badge} href={item.href} icon={item.icon} isActive={item.isActive} isCollapsed={isSidebarCollapsed} key={`${group.id}-${item.label}`} label={item.label} />)}</div></div>;
      $[34] = isSidebarCollapsed;
      $[35] = t25;
    } else {
      t25 = $[35];
    }
    t24 = content.sidebar.groups.map(t25);
    $[31] = content.sidebar.groups;
    $[32] = isSidebarCollapsed;
    $[33] = t24;
  } else {
    t24 = $[33];
  }
  let t25;
  if ($[36] !== t24) {
    t25 = <nav className="flex-1 space-y-5 overflow-y-auto px-1 py-2">{t24}</nav>;
    $[36] = t24;
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  const t26 = isSidebarCollapsed && "justify-center";
  let t27;
  if ($[38] !== t26) {
    t27 = cn("flex items-center gap-3 transition-all duration-300", t26);
    $[38] = t26;
    $[39] = t27;
  } else {
    t27 = $[39];
  }
  let t28;
  if ($[40] !== content.user.avatarUrl || $[41] !== content.user.shortName) {
    t28 = <div className="inline-flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[linear-gradient(135deg,var(--primary)_0%,var(--accent)_100%)] text-sm font-semibold text-white shadow-[0_18px_40px_rgba(21,90,125,0.2)]"><UserAvatar avatarUrl={content.user.avatarUrl} imgClassName="size-11" shortName={content.user.shortName} /></div>;
    $[40] = content.user.avatarUrl;
    $[41] = content.user.shortName;
    $[42] = t28;
  } else {
    t28 = $[42];
  }
  const t29 = isSidebarCollapsed ? "w-0 opacity-0" : "w-auto opacity-100";
  let t30;
  if ($[43] !== t29) {
    t30 = cn("min-w-0 transition-[width,opacity] duration-300", t29);
    $[43] = t29;
    $[44] = t30;
  } else {
    t30 = $[44];
  }
  let t31;
  if ($[45] !== content.user.name) {
    t31 = <p className="truncate text-sm font-semibold text-slate-950">{content.user.name}</p>;
    $[45] = content.user.name;
    $[46] = t31;
  } else {
    t31 = $[46];
  }
  let t32;
  if ($[47] !== content.user.email) {
    t32 = <p className="truncate text-xs text-slate-500">{content.user.email}</p>;
    $[47] = content.user.email;
    $[48] = t32;
  } else {
    t32 = $[48];
  }
  let t33;
  if ($[49] !== t30 || $[50] !== t31 || $[51] !== t32) {
    t33 = <div className={t30}>{t31}{t32}</div>;
    $[49] = t30;
    $[50] = t31;
    $[51] = t32;
    $[52] = t33;
  } else {
    t33 = $[52];
  }
  let t34;
  if ($[53] !== t27 || $[54] !== t28 || $[55] !== t33) {
    t34 = <div className={t27}>{t28}{t33}</div>;
    $[53] = t27;
    $[54] = t28;
    $[55] = t33;
    $[56] = t34;
  } else {
    t34 = $[56];
  }
  const t35 = isSidebarCollapsed ? "max-h-0 overflow-hidden opacity-0" : "max-h-48 opacity-100";
  let t36;
  if ($[57] !== t35) {
    t36 = cn("mt-4 space-y-3 transition-[max-height,opacity] duration-300", t35);
    $[57] = t35;
    $[58] = t36;
  } else {
    t36 = $[58];
  }
  let t37;
  if ($[59] !== content.sidebar.footer.logoutLabel || $[60] !== content.sidebar.footer.logoutLoadingLabel || $[61] !== content.sidebar.footer.logoutRedirectTo) {
    t37 = <LogoutButton className="w-full justify-center rounded-2xl" label={content.sidebar.footer.logoutLabel} loadingLabel={content.sidebar.footer.logoutLoadingLabel} redirectTo={content.sidebar.footer.logoutRedirectTo} variant="ghost" />;
    $[59] = content.sidebar.footer.logoutLabel;
    $[60] = content.sidebar.footer.logoutLoadingLabel;
    $[61] = content.sidebar.footer.logoutRedirectTo;
    $[62] = t37;
  } else {
    t37 = $[62];
  }
  let t38;
  if ($[63] !== t36 || $[64] !== t37) {
    t38 = <div className={t36}>{t37}</div>;
    $[63] = t36;
    $[64] = t37;
    $[65] = t38;
  } else {
    t38 = $[65];
  }
  let t39;
  if ($[66] !== t34 || $[67] !== t38) {
    t39 = <div className="rounded-[1.5rem] bg-slate-50/90 p-3">{t34}{t38}</div>;
    $[66] = t34;
    $[67] = t38;
    $[68] = t39;
  } else {
    t39 = $[68];
  }
  let t40;
  if ($[69] !== t23 || $[70] !== t25 || $[71] !== t39 || $[72] !== t8) {
    t40 = <aside className={t8}>{t23}{t25}{t39}</aside>;
    $[69] = t23;
    $[70] = t25;
    $[71] = t39;
    $[72] = t8;
    $[73] = t40;
  } else {
    t40 = $[73];
  }
  const t41 = isSidebarCollapsed ? "lg:ml-[112px]" : "lg:ml-[262px]";
  let t42;
  if ($[74] !== t41) {
    t42 = cn("min-w-0 px-0 sm:px-0 lg:py-4 lg:pr-4", t41);
    $[74] = t41;
    $[75] = t42;
  } else {
    t42 = $[75];
  }
  const t43 = "mx-auto w-full max-w-7xl";
  const t44 = isScrolled ? "px-4 py-4 sm:px-5" : "px-4 py-5 sm:px-6 sm:py-6";
  let t45;
  if ($[76] !== t44) {
    t45 = cn("sticky top-3 z-20 overflow-hidden rounded-xl bg-[linear-gradient(135deg,var(--primary)_0%,var(--accent)_72%,#4b8ef3_100%)] text-white shadow-[0_28px_90px_rgba(21,90,125,0.28)] transition-all duration-500 lg:top-4", t44);
    $[76] = t44;
    $[77] = t45;
  } else {
    t45 = $[77];
  }
  let t46;
  if ($[78] !== setIsMobileSidebarOpen) {
    t46 = () => setIsMobileSidebarOpen(true);
    $[78] = setIsMobileSidebarOpen;
    $[79] = t46;
  } else {
    t46 = $[79];
  }
  let t47;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <Menu className="size-4.5" />;
    $[80] = t47;
  } else {
    t47 = $[80];
  }
  let t48;
  if ($[81] !== content.header.openSidebarLabel || $[82] !== t46) {
    t48 = <button aria-label={content.header.openSidebarLabel} className="inline-flex size-10 items-center justify-center rounded-full border border-white/16 bg-white/10 text-white backdrop-blur-sm transition hover:bg-white/16 lg:hidden" onClick={t46} type="button">{t47}</button>;
    $[81] = content.header.openSidebarLabel;
    $[82] = t46;
    $[83] = t48;
  } else {
    t48 = $[83];
  }
  const t49 = isSidebarCollapsed ? content.header.expandSidebarLabel : content.header.collapseSidebarLabel;
  let t50;
  if ($[84] !== toggleSidebar) {
    t50 = () => toggleSidebar();
    $[84] = toggleSidebar;
    $[85] = t50;
  } else {
    t50 = $[85];
  }
  let t51;
  if ($[86] !== isSidebarCollapsed) {
    t51 = isSidebarCollapsed ? <PanelLeftOpen className="size-4.5" /> : <PanelLeftClose className="size-4.5" />;
    $[86] = isSidebarCollapsed;
    $[87] = t51;
  } else {
    t51 = $[87];
  }
  let t52;
  if ($[88] !== t49 || $[89] !== t50 || $[90] !== t51) {
    t52 = <button aria-label={t49} className="hidden size-10 items-center justify-center rounded-full border border-white/16 bg-white/10 text-white backdrop-blur-sm transition hover:bg-white/16 lg:inline-flex" onClick={t50} type="button">{t51}</button>;
    $[88] = t49;
    $[89] = t50;
    $[90] = t51;
    $[91] = t52;
  } else {
    t52 = $[91];
  }
  let t53;
  if ($[92] !== content.user.avatarUrl || $[93] !== content.user.shortName) {
    t53 = <div className="inline-flex size-10 items-center justify-center overflow-hidden rounded-full border border-white/12 bg-white/15 text-sm font-semibold text-white shadow-[0_12px_24px_rgba(11,32,67,0.15)]"><UserAvatar avatarUrl={content.user.avatarUrl} imgClassName="size-10" shortName={content.user.shortName} /></div>;
    $[92] = content.user.avatarUrl;
    $[93] = content.user.shortName;
    $[94] = t53;
  } else {
    t53 = $[94];
  }
  const t54 = content.role === "seeker" ? "/dashboard/seeker" : undefined;
  let t55;
  if ($[95] !== startSearchTransition) {
    t55 = value => startSearchTransition(() => {
      setSearchQuery(value);
    });
    $[95] = startSearchTransition;
    $[96] = t55;
  } else {
    t55 = $[96];
  }
  let t56;
  if ($[97] !== content.header.searchLabel || $[98] !== content.header.searchPlaceholder || $[99] !== content.locale || $[100] !== t54 || $[101] !== t55) {
    t56 = <DashboardSearch dashboardBasePath={t54} locale={content.locale} onQueryChange={t55} searchLabel={content.header.searchLabel} searchPlaceholder={content.header.searchPlaceholder} />;
    $[97] = content.header.searchLabel;
    $[98] = content.header.searchPlaceholder;
    $[99] = content.locale;
    $[100] = t54;
    $[101] = t55;
    $[102] = t56;
  } else {
    t56 = $[102];
  }
  let t57;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <NotificationBell />;
    $[103] = t57;
  } else {
    t57 = $[103];
  }
  let t58;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <span className="opacity-40" />;
    $[104] = t58;
  } else {
    t58 = $[104];
  }
  let t59;
  if ($[105] !== content.header.dateValue) {
    t59 = <span>{content.header.dateValue},</span>;
    $[105] = content.header.dateValue;
    $[106] = t59;
  } else {
    t59 = $[106];
  }
  let t60;
  if ($[107] !== content.header.timeValue) {
    t60 = <span>{content.header.timeValue}</span>;
    $[107] = content.header.timeValue;
    $[108] = t60;
  } else {
    t60 = $[108];
  }
  let t61;
  if ($[109] !== t59 || $[110] !== t60) {
    t61 = <div className="hidden items-center gap-1.5 rounded-full border border-white/12 bg-white/10 px-3 py-2 text-xs font-medium text-white/88 backdrop-blur-sm sm:inline-flex">{t58}{t59}{t60}</div>;
    $[109] = t59;
    $[110] = t60;
    $[111] = t61;
  } else {
    t61 = $[111];
  }
  let t62;
  if ($[112] !== t48 || $[113] !== t52 || $[114] !== t53 || $[115] !== t56 || $[116] !== t61) {
    t62 = <div className="flex items-center gap-2 sm:gap-3">{t48}{t52}{t53}{t56}{t57}{t61}</div>;
    $[112] = t48;
    $[113] = t52;
    $[114] = t53;
    $[115] = t56;
    $[116] = t61;
    $[117] = t62;
  } else {
    t62 = $[117];
  }
  const t63 = isScrolled ? "mt-3 grid-rows-[0fr] opacity-0" : "mt-5 grid-rows-[1fr] opacity-100 sm:mt-6";
  let t64;
  if ($[118] !== t63) {
    t64 = cn("grid overflow-hidden transition-[grid-template-rows,margin,opacity] duration-500", t63);
    $[118] = t63;
    $[119] = t64;
  } else {
    t64 = $[119];
  }
  let t65;
  if ($[120] !== content.hero.title) {
    t65 = <h1 className="text-3xl font-semibold tracking-tight text-white sm:text-[2rem]">{content.hero.title}</h1>;
    $[120] = content.hero.title;
    $[121] = t65;
  } else {
    t65 = $[121];
  }
  let t66;
  if ($[122] !== content.hero.description) {
    t66 = <p className="mt-2 max-w-2xl text-sm leading-7 text-white/84 sm:text-sm">{content.hero.description}</p>;
    $[122] = content.hero.description;
    $[123] = t66;
  } else {
    t66 = $[123];
  }
  let t67;
  if ($[124] !== content.hero.reminderLabel) {
    t67 = <span className="font-medium">{content.hero.reminderLabel}:</span>;
    $[124] = content.hero.reminderLabel;
    $[125] = t67;
  } else {
    t67 = $[125];
  }
  let t68;
  if ($[126] !== content.hero.reminderText) {
    t68 = <span>{content.hero.reminderText}</span>;
    $[126] = content.hero.reminderText;
    $[127] = t68;
  } else {
    t68 = $[127];
  }
  let t69;
  if ($[128] !== t67 || $[129] !== t68) {
    t69 = <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/10 px-4 py-2 text-xs text-white/90">{t67}{t68}</div>;
    $[128] = t67;
    $[129] = t68;
    $[130] = t69;
  } else {
    t69 = $[130];
  }
  let t70;
  if ($[131] !== t65 || $[132] !== t66 || $[133] !== t69) {
    t70 = <div className="max-w-3xl">{t65}{t66}{t69}</div>;
    $[131] = t65;
    $[132] = t66;
    $[133] = t69;
    $[134] = t70;
  } else {
    t70 = $[134];
  }
  let t71;
  if ($[135] !== content.hero.quickActions) {
    t71 = content.hero.quickActions.map(_temp4);
    $[135] = content.hero.quickActions;
    $[136] = t71;
  } else {
    t71 = $[136];
  }
  let t72;
  if ($[137] !== t71) {
    t72 = <div className="grid grid-cols-3 gap-1 sm:grid-cols-6 xl:min-w-[560px]">{t71}</div>;
    $[137] = t71;
    $[138] = t72;
  } else {
    t72 = $[138];
  }
  let t73;
  if ($[139] !== t70 || $[140] !== t72) {
    t73 = <div className="min-h-0 overflow-hidden"><div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">{t70}{t72}</div></div>;
    $[139] = t70;
    $[140] = t72;
    $[141] = t73;
  } else {
    t73 = $[141];
  }
  let t74;
  if ($[142] !== t64 || $[143] !== t73) {
    t74 = <div className={t64}>{t73}</div>;
    $[142] = t64;
    $[143] = t73;
    $[144] = t74;
  } else {
    t74 = $[144];
  }
  const t75 = isScrolled ? "mt-3 max-h-24 opacity-100" : "max-h-0 opacity-0";
  let t76;
  if ($[145] !== t75) {
    t76 = cn("overflow-hidden transition-[max-height,opacity,margin] duration-300", t75);
    $[145] = t75;
    $[146] = t76;
  } else {
    t76 = $[146];
  }
  let t77;
  if ($[147] !== content.hero.title) {
    t77 = <p className="text-lg font-semibold text-white">{content.hero.title}</p>;
    $[147] = content.hero.title;
    $[148] = t77;
  } else {
    t77 = $[148];
  }
  let t78;
  if ($[149] !== content.hero.reminderText) {
    t78 = <p className="text-sm text-white/76">{content.hero.reminderText}</p>;
    $[149] = content.hero.reminderText;
    $[150] = t78;
  } else {
    t78 = $[150];
  }
  let t79;
  if ($[151] !== t77 || $[152] !== t78) {
    t79 = <div>{t77}{t78}</div>;
    $[151] = t77;
    $[152] = t78;
    $[153] = t79;
  } else {
    t79 = $[153];
  }
  let t80;
  if ($[154] !== content.hero.quickActions) {
    t80 = content.hero.quickActions.slice(0, 3).map(_temp5);
    $[154] = content.hero.quickActions;
    $[155] = t80;
  } else {
    t80 = $[155];
  }
  let t81;
  if ($[156] !== t80) {
    t81 = <div className="flex items-center gap-2">{t80}</div>;
    $[156] = t80;
    $[157] = t81;
  } else {
    t81 = $[157];
  }
  let t82;
  if ($[158] !== t79 || $[159] !== t81) {
    t82 = <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">{t79}{t81}</div>;
    $[158] = t79;
    $[159] = t81;
    $[160] = t82;
  } else {
    t82 = $[160];
  }
  let t83;
  if ($[161] !== t76 || $[162] !== t82) {
    t83 = <div className={t76}>{t82}</div>;
    $[161] = t76;
    $[162] = t82;
    $[163] = t83;
  } else {
    t83 = $[163];
  }
  let t84;
  if ($[164] !== t45 || $[165] !== t62 || $[166] !== t74 || $[167] !== t83) {
    t84 = <section className={t45}>{t62}{t74}{t83}</section>;
    $[164] = t45;
    $[165] = t62;
    $[166] = t74;
    $[167] = t83;
    $[168] = t84;
  } else {
    t84 = $[168];
  }
  const t85 = children ?? <><section className="mt-4 grid gap-3 xl:grid-cols-5" id="overview">{content.stats.map(_temp6)}</section><section className="mt-6" id="activity"><div className="mb-4"><h2 className="text-xl font-semibold tracking-tight text-slate-950">{content.activity.title}</h2></div><div className="grid gap-4 xl:grid-cols-3">{content.activity.columns.map(column => {
          const columnState = filteredActivityColumns.find(state => state.id === column.id);
          return <ActivityColumnCard column={column} emptyStateLabel={content.activity.emptyStateLabel} items={columnState?.items ?? []} key={column.id} />;
        })}</div></section><section className="mt-6 pb-8" id="tasks"><div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"><h2 className="text-xl font-semibold tracking-tight text-slate-950">{content.tasks.title}</h2><button className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white/92 px-4 py-2 text-sm font-medium text-slate-700 shadow-[0_12px_28px_rgba(15,23,42,0.04)] transition hover:bg-white" onClick={() => setIsTasksExpanded(_temp7)} type="button">{isTasksExpanded ? content.tasks.collapseLabel : content.tasks.expandLabel}</button></div><div className="grid gap-4 xl:grid-cols-4">{content.tasks.columns.map(column_0 => {
          const columnState_0 = filteredTaskColumns.find(state_0 => state_0.id === column_0.id);
          return <TaskColumnCard column={column_0} emptyStateLabel={content.tasks.emptyStateLabel} isExpanded={isTasksExpanded} items={columnState_0?.items ?? []} key={column_0.id} />;
        })}</div></section></>;
  let t86;
  if ($[169] !== t84 || $[170] !== t85) {
    t86 = <div className={t43}>{t84}{t85}</div>;
    $[169] = t84;
    $[170] = t85;
    $[171] = t86;
  } else {
    t86 = $[171];
  }
  let t87;
  if ($[172] !== t42 || $[173] !== t86) {
    t87 = <main className={t42}>{t86}</main>;
    $[172] = t42;
    $[173] = t86;
    $[174] = t87;
  } else {
    t87 = $[174];
  }
  let t88;
  if ($[175] !== t40 || $[176] !== t87) {
    t88 = <div className={t5}>{t40}{t87}</div>;
    $[175] = t40;
    $[176] = t87;
    $[177] = t88;
  } else {
    t88 = $[177];
  }
  let t89;
  if ($[178] !== t4 || $[179] !== t88) {
    t89 = <div className={t3}>{t4}{t88}</div>;
    $[178] = t4;
    $[179] = t88;
    $[180] = t89;
  } else {
    t89 = $[180];
  }
  return t89;
}
function _temp7(value_0) {
  return !value_0;
}
function _temp6(stat) {
  return <StatCard key={stat.id} stat={stat} />;
}
function _temp5(action_0) {
  const Icon_0 = ICON_MAP[action_0.icon];
  return <Link aria-label={action_0.label} className="inline-flex size-10 items-center justify-center rounded-full border border-white/12 bg-white/10 text-white transition hover:bg-white/16" href={action_0.href} key={action_0.label} title={action_0.label}><Icon_0 className="size-4.5" /></Link>;
}
function _temp4(action) {
  const Icon = ICON_MAP[action.icon];
  return <Link className="group flex flex-col items-center gap-1 rounded-xl text-center transition" href={action.href} key={action.label}><span className="inline-flex size-11 items-center justify-center rounded-full border border-white/14 bg-white/12 text-white shadow-[0_10px_20px_rgba(17,38,84,0.14)] transition group-hover:scale-105"><Icon className="size-4.5" /></span><span className="text-xs font-medium text-white/90">{action.label}</span></Link>;
}